import { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { Checkbox } from "@/components/Interface/FormFields/Checkbox/Checkbox";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { PasswordStrength } from "@/components/Interface/PasswordStrength/PasswordStrength";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useCreateCustomer } from "@/services/api";
import { CreateCustomerRequest } from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes } from "@/utils/atoms";
import { getLanguageId } from "@/utils/customerUtils";
import {
  getPasswordRequirementTokens,
  getPasswordValidationError,
} from "@/utils/passwordValidation";
import { createRegisterErrorHandler } from "@/utils/registerErrorHandler";

import { WelcomeCard } from "./WelcomeCard";

interface Props {
  afterRegister?: () => void | Promise<void>;
  handleVerifyEmailModalClose?: () => void;
}

type RegisterFormFields = {
  confirmPassword: string;
  emailAddress: string;
  password: string;
  termsAccepted: boolean;
};

export const RegisterForm: FCC<Props> = ({
  children,
  handleVerifyEmailModalClose,
  afterRegister,
}) => {
  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormFields>({
    defaultValues: {
      confirmPassword: "",
      emailAddress: "",
      password: "",
      termsAccepted: false,
    },
    mode: "onChange",
  });

  const { i18n, t } = useTranslation();
  const { shouldShowVerifyEmailModal, setShouldShowVerifyEmailModal } =
    useCustomerLogin();
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [showPasswordStrength, setShowPasswordStrength] =
    useState<boolean>(false);

  const [isAfterRegisterLoginLoading, setIsAfterRegisterLoginLoading] =
    useState(false);
  const registerErrorHandler = createRegisterErrorHandler(
    setError,
    t,
    showAlert,
  );

  const baseLanguage = i18n.language.split("-")[0];

  const { mutate: createCustomer, isLoading } = useCreateCustomer({
    request: {
      headers: {
        "X-Skip-Auth": "true",
      },
    },
    mutation: {
      onSuccess: () => {
        showAlert({
          text: t(
            "translation:newWelcome.registerForm.uiFeedback.registrationSuccess",
          ),
          type: AlertTypes.success,
        });

        // Start login process after successful registration
        setIsAfterRegisterLoginLoading(true);
        // Show verifyEmailModal to proceed with Login (user will be authorized after finishing email verification)
        setShouldShowVerifyEmailModal(true);
      },
      onError: (error) => {
        registerErrorHandler(error);
      },
    },
  });

  const onSubmit = (data: RegisterFormFields) => {
    if (data.termsAccepted !== true) {
      setError("termsAccepted", {
        type: "manual",
        message: t(
          "translation:newWelcome.registerForm.validation.acceptTerms",
        ),
      });

      return;
    }

    const createCustomerRequest: CreateCustomerRequest = {
      header: {
        client: 1,
        source: 1,
        lastUser: 1,
      },
      basicCustomerData: {
        language: { id: getLanguageId(t, baseLanguage) },
        email: data.emailAddress,
        mainAddress: {
          country: { id: 200 },
          zip: "0000",
          city: "Dummy",
          street: "Dummy",
        },
      },
      authentification: {
        login: data.emailAddress,
        password: data.password,
      },
    };

    createCustomer({ data: createCustomerRequest });
  };

  const passwordRequirements = getPasswordRequirementTokens(t);

  return (
    <>
      <WelcomeCard>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-6"
        >
          <h1 className="text-headline-md font-semibold leading-sm text-foreground-primary">
            {t("newWelcome.registerForm.title")}
          </h1>

          <div className="flex flex-col gap-6">
            <FormField<RegisterFormFields>
              id="emailAddress"
              name="emailAddress"
              label={t("translation:Common.generalForm.fields.email.label")}
              type="email"
              register={register}
              errors={errors}
              options={{
                required: {
                  value: true,
                  message: t(
                    "translation:Common.generalForm.validation.emailRequired",
                  ),
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t(
                    "translation:Common.generalForm.validation.emailNotValid",
                  ),
                },
              }}
            />

            <div>
              <PasswordField<RegisterFormFields>
                id="password"
                name="password"
                label={t(
                  "translation:Common.generalForm.fields.password.label",
                )}
                register={register}
                errors={errors}
                options={{
                  required: {
                    value: true,
                    message: t(
                      "translation:Common.generalForm.validation.passwordRequired",
                    ),
                  },
                  validate: (value: string) =>
                    getPasswordValidationError(value, t),
                }}
                onFocus={() => {
                  setShowPasswordStrength(true);
                }}
                onBlur={() => {
                  setShowPasswordStrength(false);
                }}
              />
              {showPasswordStrength && (
                <div className="mt-2">
                  <PasswordStrength
                    password={watch("password")}
                    validationTokens={passwordRequirements}
                    inverted={false}
                  />
                </div>
              )}
            </div>

            <PasswordField<RegisterFormFields>
              id="confirm-password"
              name="confirmPassword"
              label={t(
                "translation:newWelcome.registerForm.confirmPassword.label",
              )}
              register={register}
              errors={errors}
              options={{
                required: {
                  value: true,
                  message: t(
                    "translation:newWelcome.registerForm.validation.confirmPasswordRequired",
                  ),
                },
                validate: (value: string) =>
                  value === watch("password") ||
                  t(
                    "translation:newWelcome.registerForm.validation.passwordNoMatch",
                  ),
              }}
              //! Workaround since onBlur doesn't work on passwordConfirm
              onFocus={() => {
                setShowPasswordStrength(false);
              }}
            />

            <div>
              <Checkbox
                id="termsAccepted"
                name="termsAccepted"
                labelComponent={
                  <p className="max-w-full text-body-xs leading-md text-foreground-primary">
                    <Trans
                      i18nKey="translation:newWelcome.registerForm.acceptTerms"
                      components={{
                        1: (
                          <a
                            href={t("translation:Common.links.privacyPolicy")}
                            className="inline w-fit border-b border-solid border-foreground-primary font-semibold text-foreground-primary hover:-mb-px hover:border-b-2 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-4 focus-visible:!outline-foreground-primary"
                            target="_blank"
                            rel="noreferrer"
                          />
                        ),
                        2: (
                          <a
                            href={t(
                              "translation:Common.links.terms&Conditions",
                            )}
                            className="inline w-fit border-b border-solid border-foreground-primary font-semibold text-foreground-primary hover:-mb-px hover:border-b-2 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-4 focus-visible:!outline-foreground-primary"
                            target="_blank"
                            rel="noreferrer"
                          />
                        ),
                      }}
                    />
                  </p>
                }
                register={register}
                options={{
                  required: {
                    value: true,
                    message: t(
                      "translation:newWelcome.registerForm.validation.acceptTerms",
                    ),
                  },
                }}
                errors={errors}
              />

              {shouldShowVerifyEmailModal && (
                <VerifyEmailModal
                  email={watch("emailAddress")}
                  password={watch("password")}
                  handleClose={() => {
                    setShouldShowVerifyEmailModal(false);
                    setIsAfterRegisterLoginLoading(false);

                    if (!!handleVerifyEmailModalClose) {
                      handleVerifyEmailModalClose();
                    }
                  }}
                  handleSuccess={async () => {
                    setShouldShowVerifyEmailModal(false);

                    if (afterRegister) {
                      await afterRegister();
                    }

                    navigate(portalRoutes.personalDataOnboarding, {
                      replace: true,
                    });
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              variant="primary"
              disabled={
                isLoading || isSubmitting || isAfterRegisterLoginLoading
              }
              type="submit"
            >
              {isAfterRegisterLoginLoading ? (
                <LoadingSpinner />
              ) : (
                t("translation:newWelcome.registerForm.title")
              )}
            </Button>
          </div>
        </form>
      </WelcomeCard>

      <div className="flex justify-center py-3">{children}</div>
    </>
  );
};
